import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Flex, ButtonV2, TextV2, IconV2, InputV2, Toggle, TooltipV2 } from '@withjoy/joykit';
import { Copy, Settings, OpenInNewWindow, Help, Send } from '@withjoy/joykit/icons';
import { FormikProps } from 'formik';
import { useYourMagicLinkController } from './YourMagicLink.controller';
import { ToggleTitle } from '@apps/contactCollector/routes/Create/components/Editor/Editor.styles';
import { ContactCollectorInputFields } from '@apps/contactCollector/ContactCollector.controller';

interface YourMagicLinkProps
  extends Readonly<{
    magicLink: string;
    formik: FormikProps<ContactCollectorInputFields>;
  }> {}

export const YourMagicLink: React.FC<YourMagicLinkProps> = ({ magicLink, formik }) => {
  const {
    tq,
    URL,
    FULL_URL,
    isContactCollectorActive,
    isCollectHouseholdEnabled,
    isGroupMessagingAvailable,
    isSmsAvailable,
    handleOnCopyClick,
    handleOnModifyDesignClick,
    handleOnPreviewClick,
    handleOnToggleChange,
    handleOnSendToGuestsClick,
    mutation: { isMutationLoading, mutationHasError },
  } = useYourMagicLinkController(magicLink, formik);

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      width={['100%', null, '375px']}
      marginTop="48px"
    >
      <TextV2
        typographyVariant="hed3"
        marginBottom="16px"
      >
        {tq`Settings`}
      </TextV2>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        paddingBottom={5}
        width="100%"
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
        >
          <ToggleTitle>{tq`Collect guest details`}</ToggleTitle>
        </Flex>
        <Toggle
          hideLabel
          intent="neutral"
          label={tq`Collect guest details`}
          checked={isContactCollectorActive}
          onChange={() => handleOnToggleChange('enableContactCollector')}
          disabled={isMutationLoading}
        />
      </Flex>
      {!isContactCollectorActive && (
        <TextV2
          typographyVariant="label1"
          color="mono12"
          marginBottom="8px"
        >
          {tq`Your Contact Collector is disabled. The link won't be available until you activate it.`}
        </TextV2>
      )}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        paddingBottom={5}
        width="100%"
      >
        <Flex
          alignItems="center"
          columnGap="5px"
        >
          <ToggleTitle>{tq`Allow guests to add +1s`}</ToggleTitle>
          <TooltipV2
            content={tq`Allow guests to add their family members or plus one to their contact information.`}
            placement="bottom"
            onMouseEnterDelay={500}
          >
            <IconV2 size="sm">
              <Help />
            </IconV2>
          </TooltipV2>
        </Flex>
        <Toggle
          hideLabel
          intent="neutral"
          label={tq`Allow guests to add +1s`}
          checked={isCollectHouseholdEnabled}
          onChange={() => handleOnToggleChange('enableCollectHousehold')}
          disabled={isMutationLoading}
        />
      </Flex>
      {/* {!isCollectHouseholdEnabled && (
        <TextV2 typographyVariant="label1" color="mono12" marginBottom="8px">
          {tAdmin.disabledCollectHouseholdHelper()}
        </TextV2>
      )} */}
      {mutationHasError && (
        <TextV2
          typographyVariant="label1"
          color="negative5"
        >
          {tq`There was an error updating the Contact Collector`}
        </TextV2>
      )}
      <TextV2
        typographyVariant="hed3"
        marginTop="24px"
        marginBottom="16px"
      >
        {tq`Share`}
      </TextV2>
      <Flex
        alignItems="center"
        columnGap="8px"
        marginBottom="16px"
      >
        <InputV2
          disabled
          value={URL}
          overrides={{
            Input: {
              props: {
                color: 'mono8',
                cursor: 'text',
              },
            },
          }}
        />
        {isGroupMessagingAvailable && (
          <CopyToClipboard text={FULL_URL}>
            <ButtonV2
              variant="ghost"
              intent="neutral"
              shape="rounded"
              onClick={handleOnCopyClick}
              disabled={!isContactCollectorActive}
              aria-label={tq`Copy Link`}
              padding="8px"
              minWidth="auto"
            >
              <IconV2 size="md">
                <Copy />
              </IconV2>
            </ButtonV2>
          </CopyToClipboard>
        )}
      </Flex>
      {!isGroupMessagingAvailable && (
        <CopyToClipboard text={FULL_URL}>
          <ButtonV2
            variant="outline"
            intent="neutral"
            shape="rounded"
            width="100%"
            marginBottom="24px"
            onClick={handleOnCopyClick}
            disabled={!isContactCollectorActive}
          >
            <IconV2
              size="md"
              marginRight="8px"
            >
              <Copy />
            </IconV2>{' '}
            {tq`Copy Link`}
          </ButtonV2>
        </CopyToClipboard>
      )}
      {isGroupMessagingAvailable && (
        <>
          <ButtonV2
            intent="newPrimary"
            shape="rounded"
            width="100%"
            marginBottom="8px"
            onClick={handleOnSendToGuestsClick}
            disabled={!isContactCollectorActive}
          >
            <IconV2
              size="md"
              marginRight="8px"
            >
              <Send />
            </IconV2>{' '}
            {tq`Share with Guests`}
          </ButtonV2>
          <TextV2
            typographyVariant="label1"
            color="mono10"
            marginBottom="24px"
            textAlign="center"
          >
            {isSmsAvailable ? tq`Text or email everyone at once.` : tq`Email everyone at once.`}
          </TextV2>
        </>
      )}
      <Flex
        columnGap="8px"
        width="100%"
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          shape="rounded"
          onClick={handleOnModifyDesignClick}
          flex={1}
        >
          <IconV2
            size="md"
            marginRight="8px"
          >
            <Settings />
          </IconV2>{' '}
          {tq`Modify Design`}
        </ButtonV2>
        <ButtonV2
          variant="ghost"
          intent="neutral"
          shape="rounded"
          onClick={handleOnPreviewClick}
          flex={1}
          disabled={!isContactCollectorActive}
        >
          <IconV2
            size="md"
            marginRight="8px"
          >
            <OpenInNewWindow />
          </IconV2>{' '}
          {tq`Preview as Guest`}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};
